"use client";

import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, ExternalLink, Shield, ChevronDown, ChevronUp, Activity } from 'lucide-react';

const activeThreats = [
  { id: 'THR-2847', type: 'SQL Injection', severity: 'critical', asset: 'PROD-API-01', time: '2m ago' },
  { id: 'THR-2846', type: 'Brute Force', severity: 'high', asset: 'BTRC-FW-03', time: '5m ago' },
  { id: 'THR-2845', type: 'Data Exfil', severity: 'critical', asset: 'DB-PROD-01', time: '11m ago' },
  { id: 'THR-2844', type: 'Priv Escalation', severity: 'high', asset: 'WIN-SRV-14', time: '18m ago' },
];

const recentCVEs = [
  { id: 'CVE-2025-3194', cvss: 9.8, product: 'Apache 2.4.58', type: 'RCE' },
  { id: 'CVE-2026-0847', cvss: 9.1, product: 'PostgreSQL 14.2', type: 'Priv Esc' },
  { id: 'CVE-2025-44228', cvss: 8.7, product: 'Log4j (variant)', type: 'RCE' },
  { id: 'CVE-2025-7891', cvss: 7.5, product: 'OpenSSL 3.1.2', type: 'DoS' },
];

const mitreAttacks = [
  { id: 'T1190', name: 'Exploit Public-Facing App', tactic: 'Initial Access', active: true },
  { id: 'T1041', name: 'Exfiltration Over C2', tactic: 'Exfiltration', active: true },
  { id: 'T1110.001', name: 'Password Guessing', tactic: 'Credential Access', active: true },
  { id: 'T1068', name: 'Exploitation for Priv Esc', tactic: 'Privilege Escalation', active: true },
  { id: 'T1071.004', name: 'DNS Application Layer', tactic: 'C2', active: false },
];

function usePeriodicFlash(intervalMs: number) {
  const [flashIdx, setFlashIdx] = useState<number | null>(null);
  const countRef = useRef(0);

  useEffect(() => {
    const timer = setInterval(() => {
      const idx = Math.floor(Math.random() * countRef.current);
      setFlashIdx(idx);
      setTimeout(() => setFlashIdx(null), 900);
    }, intervalMs);
    return () => clearInterval(timer);
  }, [intervalMs]);

  return { flashIdx, setCount: (n: number) => { countRef.current = n; } };
}

export default function ThreatContextPanel() {
  const [expandedSection, setExpandedSection] = useState<string | null>('threats');

  const threatFlash = usePeriodicFlash(3800);
  const cveFlash = usePeriodicFlash(5200);

  // Set counts
  threatFlash.setCount(activeThreats.length);
  cveFlash.setCount(recentCVEs.length);

  const toggle = (section: string) => {
    setExpandedSection(prev => prev === section ? null : section);
  };

  const severityColor: Record<string, string> = {
    critical: 'text-cyber-red',
    high: 'text-orange-400',
    medium: 'text-yellow-400',
  };

  return (
    <div className="flex flex-col gap-3 h-full overflow-y-auto scrollbar-cyber">
      {/* Active Threat Context */}
      <div className="cyber-card overflow-hidden">
        <button
          onClick={() => toggle('threats')}
          aria-expanded={expandedSection === 'threats'}
          aria-controls="threats-panel"
          aria-label={`Active Threat Context, ${activeThreats.length} threats`}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/3 transition-colors"
        >
          <div className="flex items-center gap-2">
            <AlertTriangle size={13} className="text-cyber-red" />
            <span className="text-xs font-semibold text-white">Active Threat Context</span>
            <span className="text-[10px] font-mono bg-cyber-red/15 text-cyber-red border border-cyber-red/25 rounded-full px-1.5 py-0.5">
              {activeThreats.length}
            </span>
          </div>
          {expandedSection === 'threats' ? <ChevronUp size={13} className="text-cyber-muted" /> : <ChevronDown size={13} className="text-cyber-muted" />}
        </button>

        {expandedSection === 'threats' && (
          <div id="threats-panel" className="divide-y divide-cyber-border/50 border-t border-cyber-border">
            {activeThreats.map((threat, i) => (
              <div
                key={threat.id}
                tabIndex={0}
                role="button"
                aria-label={`Threat ${threat.id}: ${threat.type}, ${threat.severity} severity on ${threat.asset}, ${threat.time}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                className={`px-4 py-2.5 transition-colors cursor-pointer relative overflow-hidden focus:outline-none focus:ring-1 focus:ring-cyber-blue/50 ${
                  threatFlash.flashIdx === i ? 'data-update-shimmer row-flash' : 'hover:bg-white/3'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[11px] font-mono text-cyber-blue">{threat.id}</span>
                  <span className={`text-[10px] font-mono font-medium ${severityColor[threat.severity]}`}>
                    {threat.severity}
                  </span>
                </div>
                <p className="text-[12px] text-cyber-text">{threat.type}</p>
                <p className="text-[10px] text-cyber-muted font-mono">{threat.asset} · {threat.time}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Recent CVEs */}
      <div className="cyber-card overflow-hidden">
        <button
          onClick={() => toggle('cves')}
          aria-expanded={expandedSection === 'cves'}
          aria-controls="cves-panel"
          aria-label={`Recent CVEs, ${recentCVEs.length} entries`}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/3 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Shield size={13} className="text-orange-400" />
            <span className="text-xs font-semibold text-white">Recent CVEs</span>
            <span className="text-[10px] font-mono bg-orange-500/15 text-orange-400 border border-orange-500/25 rounded-full px-1.5 py-0.5">
              {recentCVEs.length}
            </span>
          </div>
          {expandedSection === 'cves' ? <ChevronUp size={13} className="text-cyber-muted" /> : <ChevronDown size={13} className="text-cyber-muted" />}
        </button>

        {expandedSection === 'cves' && (
          <div id="cves-panel" className="divide-y divide-cyber-border/50 border-t border-cyber-border">
            {recentCVEs.map((cve, i) => (
              <div
                key={cve.id}
                tabIndex={0}
                role="button"
                aria-label={`${cve.id}: CVSS ${cve.cvss}, ${cve.product}, ${cve.type}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                className={`px-4 py-2.5 transition-colors cursor-pointer group relative overflow-hidden focus:outline-none focus:ring-1 focus:ring-cyber-blue/50 ${
                  cveFlash.flashIdx === i ? 'data-update-shimmer row-flash' : 'hover:bg-white/3'
                }`}
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[11px] font-mono text-cyber-blue group-hover:underline">{cve.id}</span>
                  <div className="flex items-center gap-1">
                    <span
                      className={`text-[10px] font-mono font-bold ${
                        cve.cvss >= 9 ? 'text-cyber-red' : cve.cvss >= 7 ? 'text-orange-400' : 'text-yellow-400'
                      }`}
                    >
                      {cve.cvss}
                    </span>
                    <ExternalLink size={9} className="text-cyber-subtle" />
                  </div>
                </div>
                <p className="text-[12px] text-cyber-text">{cve.product}</p>
                <span className="text-[9px] font-mono bg-white/5 border border-cyber-border rounded px-1.5 py-0.5 text-cyber-muted">
                  {cve.type}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* MITRE ATT&CK */}
      <div className="cyber-card overflow-hidden">
        <button
          onClick={() => toggle('mitre')}
          aria-expanded={expandedSection === 'mitre'}
          aria-controls="mitre-panel"
          aria-label={`MITRE ATT&CK techniques, ${mitreAttacks.filter(m => m.active).length} active`}
          className="w-full px-4 py-3 flex items-center justify-between hover:bg-white/3 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Activity size={13} className="text-cyber-purple" />
            <span className="text-xs font-semibold text-white">MITRE ATT&CK</span>
            <span className="text-[10px] font-mono bg-cyber-purple/15 text-cyber-purple border border-cyber-purple/25 rounded-full px-1.5 py-0.5">
              {mitreAttacks.filter(m => m.active).length} active
            </span>
          </div>
          {expandedSection === 'mitre' ? <ChevronUp size={13} className="text-cyber-muted" /> : <ChevronDown size={13} className="text-cyber-muted" />}
        </button>

        {expandedSection === 'mitre' && (
          <div id="mitre-panel" className="divide-y divide-cyber-border/50 border-t border-cyber-border">
            {mitreAttacks.map((technique) => (
              <div
                key={technique.id}
                tabIndex={0}
                role="button"
                aria-label={`${technique.id}: ${technique.name}, tactic ${technique.tactic}${technique.active ? ', currently active' : ''}`}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') e.currentTarget.click(); }}
                className="px-4 py-2.5 hover:bg-white/3 transition-colors cursor-pointer focus:outline-none focus:ring-1 focus:ring-cyber-blue/50"
              >
                <div className="flex items-center justify-between mb-0.5">
                  <span className="text-[11px] font-mono text-cyber-purple">{technique.id}</span>
                  {technique.active && (
                    <div className="flex items-center gap-1">
                      <div className="w-1.5 h-1.5 bg-cyber-red rounded-full live-dot" />
                      <span className="text-[9px] font-mono text-cyber-red">active</span>
                    </div>
                  )}
                </div>
                <p className="text-[12px] text-cyber-text">{technique.name}</p>
                <p className="text-[10px] text-cyber-muted font-mono">{technique.tactic}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Session Info */}
      <div className="cyber-card px-4 py-3">
        <p className="text-[10px] text-cyber-muted uppercase tracking-widest font-mono mb-2">Session Info</p>
        <div className="space-y-1.5 text-[11px] font-mono">
          <div className="flex justify-between">
            <span className="text-cyber-muted">Model</span>
            <span className="text-cyber-text">Gemini 1.5 Pro</span>
          </div>
          <div className="flex justify-between">
            <span className="text-cyber-muted">Context</span>
            <span className="text-cyber-text">128K tokens</span>
          </div>
          <div className="flex justify-between">
            <span className="text-cyber-muted">Messages</span>
            <span className="text-cyber-text">7 in session</span>
          </div>
          <div className="flex justify-between">
            <span className="text-cyber-muted">Mode</span>
            <span className="text-cyber-green">Security Auditor</span>
          </div>
        </div>
      </div>
    </div>
  );
}